import type { PointerEvent as ReactPointerEvent, ReactNode, RefObject } from "react";
import { useEffect, useState } from "react";

interface PanelPosition {
  left: number;
  top: number;
}

const panelPositionMemory = new Map<string, PanelPosition>();
const panelPositionStoragePrefix = "curva:panel-position:";

/** Matches `w-72` on the inspector sidebar (`RightPanel`). */
export const INSPECTOR_SIDEBAR_WIDTH = 288;
const PANEL_EDGE_GAP = 12;
const PANEL_WIDTH = 288;
const STACKED_PANEL_GAP = 44;

export const defaultPanelInitialOffset = {
  right: INSPECTOR_SIDEBAR_WIDTH + PANEL_EDGE_GAP,
  top: 112,
} as const;

export const stackedPanelInitialOffset = {
  right: defaultPanelInitialOffset.right + PANEL_WIDTH + STACKED_PANEL_GAP,
  top: 112,
} as const;

export function DraggablePanel({
  panelRef,
  storageKey,
  title,
  widthClass,
  zIndexClass = "z-40",
  initialOffset = defaultPanelInitialOffset,
  headerActions,
  children,
  onClose,
}: {
  panelRef: RefObject<HTMLDivElement | null>;
  storageKey: string;
  title: string;
  widthClass: string;
  zIndexClass?: string;
  initialOffset?: { right: number; top: number };
  headerActions?: ReactNode;
  children: ReactNode;
  onClose: () => void;
}) {
  const [position, setPosition] = useState<PanelPosition | undefined>(() => readPanelPosition(storageKey));

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !position) {
      return;
    }

    const nextPosition = clampPanelPosition(position, panel.offsetWidth, panel.offsetHeight);
    if (nextPosition.left !== position.left || nextPosition.top !== position.top) {
      setPosition(nextPosition);
      savePanelPosition(storageKey, nextPosition);
    }
  }, [panelRef, position, storageKey]);

  return (
    <div
      ref={panelRef}
      className={`fixed ${zIndexClass} min-w-0 rounded-md border border-white/10 bg-[#242a31] p-3 text-[12px] text-slate-300 shadow-2xl shadow-black/40 ${widthClass}`}
      style={position ? { left: position.left, top: position.top } : { right: initialOffset.right, top: initialOffset.top }}
    >
      <div
        className="mb-3 flex min-w-0 cursor-move touch-none items-center justify-between gap-2"
        onPointerDown={event => beginDrag(event)}
      >
        <div className="min-w-0 truncate font-medium text-slate-100">{title}</div>
        <div className="flex shrink-0 items-center gap-1">
          {headerActions}
          <button
            type="button"
            className="cursor-default rounded px-2 py-1 text-slate-400 hover:bg-white/10"
            onPointerDown={event => event.stopPropagation()}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
      {children}
    </div>
  );

  function beginDrag(event: ReactPointerEvent): void {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const rect = panel.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    commitPosition({ left: rect.left, top: rect.top });
    panel.setPointerCapture(event.pointerId);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;
      commitPosition(clampPanelPosition({
        left: moveEvent.clientX - offsetX,
        top: moveEvent.clientY - offsetY,
      }, width, height));
    };

    const handlePointerEnd = () => {
      panel.releasePointerCapture(event.pointerId);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerEnd);
    window.addEventListener("pointercancel", handlePointerEnd);
  }

  function commitPosition(nextPosition: PanelPosition): void {
    setPosition(nextPosition);
    savePanelPosition(storageKey, nextPosition);
  }
}

function readPanelPosition(key: string): PanelPosition | undefined {
  const memoryPosition = panelPositionMemory.get(key);
  if (memoryPosition) {
    return memoryPosition;
  }

  try {
    const rawValue = window.localStorage.getItem(`${panelPositionStoragePrefix}${key}`);
    if (!rawValue) {
      return undefined;
    }

    const parsedValue = JSON.parse(rawValue) as Partial<PanelPosition>;
    if (typeof parsedValue.left !== "number" || typeof parsedValue.top !== "number") {
      return undefined;
    }

    const position = {
      left: parsedValue.left,
      top: parsedValue.top,
    };
    panelPositionMemory.set(key, position);
    return position;
  } catch {
    return undefined;
  }
}

function savePanelPosition(key: string, position: PanelPosition): void {
  panelPositionMemory.set(key, position);
  try {
    window.localStorage.setItem(`${panelPositionStoragePrefix}${key}`, JSON.stringify(position));
  } catch {
    // Memory persistence still covers the current app session.
  }
}

function clampPanelPosition(position: PanelPosition, width: number, height: number): PanelPosition {
  return {
    left: clamp(position.left, 0, Math.max(0, window.innerWidth - width)),
    top: clamp(position.top, 0, Math.max(0, window.innerHeight - height)),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}
